/*
 * File: Question.jsx
 * Project: our-wave-stories-app
 *
 * Created by Brendan Michaelsen on June 13, 2024 at 9:21 AM
 * Copyright © 2024 Our Wave, Inc. All rights reserved.
 *
 * Last Modified: June 11, 2025 at 8:48 PM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import React, {
	useEffect, useMemo, useRef, useState
} from 'react';
import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Parser } from 'html-to-react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// Utilities
import { buildDynamicMeta } from '../../../components/Meta';
import { formatNumber, formatTextRedactions, truncateString } from '../../../../utilities/utilities';
import { scrollPageToHash } from '../../../utilities/position';
import { generateLongFormContentPlaceholder, generateShortFormContentPlaceholder } from '../../../utilities/placeholder';
import { toastError } from '../../../utilities/toaster';

// Services
import {
	fetchNextQuestion,
	fetchQuestion, fetchRandomQuestion, fetchRelatedQuestions, recordUpvote, removeUpvote
} from '../../../services/question';
import { recordView } from '../../../services/view';

// Store
import { updateCurrentQuestion, updateCurrentQuestionError } from '../../../store/slices/currentQuestion/currentQuestion.slice';
import { updateUser } from '../../../store/slices/user/user.slice';
import { updateCountryId } from '../../../store/slices/locale/locale.slice';

// Components
import {
	Meta,
	AppNavigation,
	SchemaScript,
	Typography,
	ObfuscatedText,
	ToolTip,
	ActionModule,
	LocaleLink,
	CommentCreator,
	CommentFeed,
	Badge,
	ContentLocalityBadge,
	ErrorComponent,
	Button,
	DetailPagePanel,
	ConfettiBurst,
} from '../../../components';

// Constants
import { AVAILABLE_I18N_NAMESPACES, SHARE_OPTIONS, SOURCES } from '../../../../constants/base.constants';

// Styles
import * as S from './Question.styles';


/**
 * Configuration
 */

const ReactParser = new Parser();


/**
 * Component
 */

const Question = ({ meta: m }) => {

	// Get component parameters
	const { slug } = useParams();

	// Get current user from hook
	const currentUser = useSelector((state) => state.user.value);

	// Get current locale from hook
	const locale = useSelector((state) => state.locale.value);

	// Get current partner from hook
	const partner = useSelector((state) => state.partner.value);

	// Get current UI mode from hook
	const uiMode = useSelector((state) => state.ui.value);

	// Create references
	const viewedQuestions = useRef([]);

	// Get current question from hook
	let { status: questionStatus, value: question } = useSelector((state) => state.currentQuestion);
	if (question?.slug && question?.slug !== slug) {
		questionStatus = 'initial';
		question = null;
	}

	// Get upvote
	const isUpvoted = useMemo(() => {

		// Get upvote schema
		const upvoteSchema = currentUser.upvote_schema || {};

		// Check for active upvote
		return upvoteSchema[question?.question_id] === true;

	}, [currentUser, question]);

	// Check if loading
	const isLoading = useMemo(() => questionStatus !== 'final', [questionStatus]);

	// Create state
	const [isUpvoteActive, setIsUpvoteActive] = useState(isUpvoted === true);
	const [upvoteCount, setUpvoteCount] = useState(question?.upvote_count);
	const [showShareWidget, setShowShareWidget] = useState(false);
	const [isFetching, setIsFetching] = useState(false);
	const [nextQuestion, setNextQuestion] = useState(null);
	const [previousQuestions, setPreviousQuestions] = useState([]);
	const [questionIndex, setQuestionIndex] = useState(0);
	const [related, setRelated] = useState(null);
	const [pendingComments, setPendingComments] = useState([]);
	const [fireConfetti, setFireConfetti] = useState(false);

	// Use hooks
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const location = useLocation();

	// Generate placeholders
	const questionPlaceholder = useMemo(() => generateShortFormContentPlaceholder(), []);
	const answerPlaceholder = useMemo(() => generateLongFormContentPlaceholder(), []);

	// Get location hash
	const { hash } = location;

	// Create refs
	const containerRef = useRef();
	const showShareWidgetRef = useRef();
	const confettiTargetRef = useRef();

	// Get translation hook
	const [t] = useTranslation(AVAILABLE_I18N_NAMESPACES.CLIENT);

	// Define meta content
	const meta = useMemo(() => ({
		title: t('pageQuestion.metaTitle', { partnerName: partner.name, questionContent: question ? formatTextRedactions(question?.question_locales[locale.localeBaseId]) : t('questionName') }),
		description: truncateString(formatTextRedactions(question?.answer_locales[locale.localeBaseId]), 160),
		bodyClasses: 'hide-captcha',
		...m || {},
		...question && question.allow_indexing === false ? {
			shouldIndex: false
		} : undefined
	}), [m, question, partner, locale, t]);

	// Define parent meta content
	const parentMeta = useMemo(() => ({
		title: t('pageLearn.metaTitle', { partnerName: partner.name })
	}), [partner, t]);

	// Create partner domain
	const partnerDomain = useMemo(() => {
		let domain = `https://${partner.domain}`;
		if (partner.redirect_to_subdomain) {
			domain = `https://${partner.redirect_to_subdomain}.${partner.domain}`;
		}
		return domain;
	}, [partner]);

	// Create answer content
	const answerContent = useMemo(() => {
		let content = question?.analytics_answer_locales[locale.localeBaseId];
		let markdown = ReactParser.parse(content);
		if (markdown && !Array.isArray(markdown)) {
			content = content.split(/\r?\n/).filter(Boolean).map((c) => `<p>${c}</p>`).join('');
			markdown = ReactParser.parse(content);
		}
		return markdown;
	}, [locale, question]);

	// Move to previous question action
	const movePrevious = () => {

		// Update next index
		setQuestionIndex(questionIndex - 1);

		// Update previous questions
		setPreviousQuestions(previousQuestions.slice(0, -1));
	};

	// Move to next question action
	const moveNext = (q) => {

		// Update next index
		setQuestionIndex(questionIndex + 1);

		// Update previous questions
		setPreviousQuestions([...previousQuestions, q]);
	};

	// Handle fetch current question
	const fetchCurrentQuestion = async () => {
		try {

			// Update state
			setRelated(null);

			// Fetch question
			const { data: { result: q } } = await fetchQuestion({ questionId: slug });

			// Update viewed questions
			viewedQuestions.current.push(q.question_id);

			// Handle redirect if necessary
			if (q.required_redirect) {

				// Get redirect parameters
				const { country_id: countryId } = q.required_redirect;

				// Update country id
				dispatch(updateCountryId({
					countryId,
					partner
				}));
			}

			// Update question
			dispatch(updateCurrentQuestion(q));

		} catch (e) {

			// Update state
			dispatch(updateCurrentQuestionError(e.message));
		}
	};

	// Handle fetch next question
	const fetchNext = async () => {
		try {

			// Fetch next question
			const { data: { result: { question: q } } } = await fetchNextQuestion({ questionId: question.question_id, viewedQuestions: viewedQuestions.current });

			// Update state
			setNextQuestion(q);

		} catch (e) {

			// Update state
			setNextQuestion(null);
		}
	};

	// Handle fetch random question
	const fetchRandom = async () => {

		// Update state
		setIsFetching(true);

		// Fetch random question
		try {

			// Fetch random question
			const { data: { result: { question: q } } } = await fetchRandomQuestion({ questionId: question.question_id, viewedQuestions: viewedQuestions.current });

			// Update state
			setIsFetching(false);

			// Update next index
			setQuestionIndex(questionIndex + 1);

			// Update previous questions
			setPreviousQuestions([...previousQuestions, question]);

			// Navigate to new question
			navigate(`${locale.localePath}/answer/${q.slug}`);

		} catch (e) {

			// Update state
			setIsFetching(false);

			// Show error
			toastError(uiMode, partner, t('pageQuestion.randomError'));
		}
	};

	// Handle fetch related questions
	const fetchRelated = async () => {
		try {

			// Fetch related questions
			const { data: { result: { questions } } } = await fetchRelatedQuestions({ questionId: question.question_id });

			// Update state
			setRelated(questions || []);

		} catch (e) {

			// Update state
			setRelated([]);
		}
	};

	// Toggle question upvote
	const toggleUpvote = async () => {

		// Handle action for state
		if (!isUpvoteActive) {

			// Update state
			setIsUpvoteActive(true);
			setUpvoteCount(upvoteCount ? upvoteCount + 1 : 1);

			// Fire confetti
			setFireConfetti(true);

			// Reset confetti
			setTimeout(() => {
				setFireConfetti(false);
			}, 3000);

			// Record upvote
			const { data: { result: { user } } } = await recordUpvote({
				source: SOURCES.QUESTION,
				entityType: SOURCES.QUESTION,
				entityId: question?.question_id,
				urlPath: window.location.pathname
			});

			// Update user
			if (user) {
				dispatch(updateUser(user));
			}

		} else {

			// Update state
			setIsUpvoteActive(false);
			setUpvoteCount(upvoteCount ? upvoteCount - 1 : 0);

			// Remove upvote
			const { data: { result: { user } } } = await removeUpvote({
				source: SOURCES.QUESTION,
				entityType: SOURCES.QUESTION,
				entityId: question?.question_id,
				urlPath: window.location.pathname
			});

			// Update user
			if (user) {
				dispatch(updateUser(user));
			}
		}
	};

	// Handle share flow
	const handleShare = () => {
		setShowShareWidget(!showShareWidget);
	};

	// Handle actions on ref change
	useEffect(() => {

		// Define click outside function
		function handleClickOutside(event) {
			if (showShareWidgetRef.current && containerRef.current && !containerRef.current.contains(event.target)) {
				setShowShareWidget(false);
			}
		}

		// Bind the event listener
		document.addEventListener('mousedown', handleClickOutside);

		// Return effect
		return () => { // Unbind the event listener on clean up
			document.removeEventListener('mousedown', handleClickOutside);
		};

	}, [containerRef]);

	// Handle actions on upvote state change
	useEffect(() => {

		// Update upvote active
		setIsUpvoteActive(isUpvoted === true);

	}, [isUpvoted]);

	// Handle actions on question upvote count change
	useEffect(() => {

		// Update count
		setUpvoteCount(question?.upvote_count);

	}, [question?.upvote_count]);

	// Handle actions on widget state
	useEffect(() => {

		// Update ref
		showShareWidgetRef.current = showShareWidget;

	}, [showShareWidget]);

	// Handle actions on url update
	useEffect(() => {
		if (questionStatus === 'final') {

			// Scroll to hash
			scrollPageToHash(hash);
		}
	}, [questionStatus, hash]);

	// Handle actions on status update
	useEffect(() => {
		if (questionStatus === 'final') {

			// Update viewed
			viewedQuestions.current = [...new Set([...viewedQuestions.current, question.question_id])];

			// Record view
			recordView({
				source: SOURCES.QUESTION,
				entityId: question.question_id,
				urlPath: window.location.pathname
			});

			// Fetch related questions
			fetchRelated();

			// Fetch next question
			fetchNext();
		}
	}, [questionStatus]);

	// Handle actions on component load
	useEffect(() => {

		// Check if should load
		if (questionStatus !== 'final') {

			// Fetch current question
			fetchCurrentQuestion();
		}

	}, [slug]);

	// Render component
	return (
		<>
			{/* Meta */}
			<Meta meta={meta} data={{}} />

			{/* Schema.org */}
			<SchemaScript schema={{
				'@context': 'http://schema.org',
				'@type': 'BreadcrumbList',
				'@id': `${partnerDomain}/answer/${question?.slug}/#BreadcrumbList`,
				itemListElement: [
					{
						'@type': 'ListItem',
						position: 1,
						item: {
							'@id': `${partnerDomain}/learn/#ListItem`,
							name: buildDynamicMeta(parentMeta.title, { partner }),
							url: `${partnerDomain}${locale.localePath}/learn`
						}
					},
					{
						'@type': 'ListItem',
						position: 2,
						item: {
							'@id': `${partnerDomain}/answer/${question?.slug}/#ListItem`,
							name: buildDynamicMeta(meta.title, { partner }),
							url: `${partnerDomain}${locale.localePath}/answer/${question?.slug}`
						}
					}
				]
			}}
			/>
			<SchemaScript schema={{
				'@context': 'https://schema.org',
				'@type': 'FAQPage',
				'@id': `${partnerDomain}/answer/${question?.slug}/#ItemPage`,
				name: buildDynamicMeta(meta.title, { partner }),
				description: buildDynamicMeta(meta.description, { partner }),
				url: `${partnerDomain}${locale.localePath}/answer/${question?.slug}`,
				inLanguage: locale.localeBaseId,
				isPartOf: {
					'@id': `${partnerDomain}/#WebSite`
				},
				publisher: {
					'@id': `${partner.website}/#Organization`
				},
				breadcrumb: {
					'@id': `${partnerDomain}/answer/${question?.slug}/#BreadcrumbList`
				},
				mainEntity: {
					'@type': 'Question',
					'@id': `${partnerDomain}/answer/${question?.slug}/#Question`,
					name: formatTextRedactions(question?.question_locales[locale.localeBaseId]),
					url: `${partnerDomain}${locale.localePath}/answer/${question?.slug}`,
					acceptedAnswer: {
						'@type': 'Answer',
						text: formatTextRedactions(question?.answer_locales[locale.localeBaseId])
					}
				}
			}}
			/>

			{/* Component Content */}
			<AppNavigation showFooter>
				<S.Wrapper>

					{/* Question Card */}
					{questionStatus !== 'error' ? (
						<>
							<S.QuestionCard>

								{/* Card Header */}
								<S.CardHeader>

									{/* Back Button */}
									<S.BackButton icon={['far', 'arrow-left']} size={1} onClick={() => { navigate(location?.state?.back?.path || `${locale.localePath}/learn`); }} />

									{/* Badges */}
									<S.BadgeContainer>

										{/* Partner Badge */}
										{question?.partner && partner?.id && question?.partner.id !== partner?.id && question?.partner.id !== 'core' && (
											<LocaleLink to={`https://${question?.partner.full_domain}`} target="_blank">
												<Badge type="neutral" variation="square" size="small">
													<Typography weight="medium">
														<Trans t={t} i18nKey="communityBadge">
															{{ partnerName: question?.partner.name }}
															{' '}
															Community
														</Trans>
													</Typography>
													<FontAwesomeIcon icon={['fas', 'arrow-up-right']} />
												</Badge>
											</LocaleLink>
										)}

									</S.BadgeContainer>

									{/* Spacer */}
									<S.Spacer />

									{/* Content Locality Badge */}
									{question && <ContentLocalityBadge country={question?.submitted_country} id={question?.question_id} />}

								</S.CardHeader>

								{/* Question */}
								<S.Question
									tag="h1"
									tagStyle="h4"
									weight="bold"
									className={isLoading && 'shimmer shimmer-element'}
								>
									<ObfuscatedText>
										{question?.question_locales[locale.localeBaseId] || questionPlaceholder}
									</ObfuscatedText>
								</S.Question>

								{/* Author Container */}
								<S.AuthorContainer>

									{/* Author Attribution */}
									<Typography
										tag="p"
										variation="3"
										className={isLoading && 'shimmer shimmer-element'}
									>
										<Trans t={t} i18nKey="pageQuestion.authorAttribution">
											Answer written by a
											{' '}
											<Typography weight="bold" tag="span">PhD Prepared Mental Health Nurse</Typography>
										</Trans>
									</Typography>

								</S.AuthorContainer>

								{/* Tag Container */}
								{question?.tags.length > 0 && (
									<S.TagContainer>

										{/* Title */}
										<Typography weight="semibold">
											<Trans t={t} i18nKey="pageQuestion.topics">
												Topics:
											</Trans>
										</Typography>

										{/* Tags */}
										{question?.tags.map((tag) => (
											<LocaleLink to={`/learn?tags=${tag.slug}`} key={tag.tag_id}>
												<Badge type="neutral" variation="square" size="small">
													<Typography weight="medium">
														{tag.title_locales[locale.localeBaseId]}
													</Typography>
												</Badge>
											</LocaleLink>
										))}

									</S.TagContainer>
								)}

								{/* Answer */}
								<S.Answer className={isLoading ? 'shimmer shimmer-element paragraphContent' : 'paragraphContent'}>
									<ObfuscatedText>
										{answerContent || answerPlaceholder}
									</ObfuscatedText>
								</S.Answer>

								{/* Navigation Bar */}
								<S.NavigationBar>

									{/* Previous Button */}
									{previousQuestions.length > questionIndex - 1 && previousQuestions.length > 0 ? (
										<LocaleLink to={`/answer/${previousQuestions[questionIndex - 1].slug}`} onClick={movePrevious}>
											<Button disabled={isFetching} variation="secondary">
												<FontAwesomeIcon icon={['fas', 'arrow-left-long']} />
												<Typography tag="span" weight="semibold">
													<Trans t={t} i18nKey="pageQuestion.previousQuestion">
														Previous Question
													</Trans>
												</Typography>
											</Button>
										</LocaleLink>
									) : <div />}

									{/* Random Button */}
									{question && (
										<Button disabled={isFetching} isLoading={isFetching} onClick={fetchRandom} variation="secondary">
											<Typography tag="span" weight="semibold">
												<Trans t={t} i18nKey="pageQuestion.randomQuestion">
													Random Question
												</Trans>
											</Typography>
											<FontAwesomeIcon icon={['fas', 'shuffle']} />
										</Button>
									)}

									{/* Next Button */}
									{nextQuestion ? (
										<LocaleLink to={`/answer/${nextQuestion.slug}`} onClick={() => { moveNext(question); }}>
											<Button disabled={isFetching} variation="secondary">
												<Typography tag="span" weight="semibold">
													<Trans t={t} i18nKey="pageQuestion.nextQuestion">
														Next Question
													</Trans>
												</Typography>
												<FontAwesomeIcon icon={['fas', 'arrow-right-long']} />
											</Button>
										</LocaleLink>
									) : <div />}

								</S.NavigationBar>

								{/* Action Bar */}
								<S.ActionBar>

									{/* Action - Upvote */}
									<ActionModule
										onClick={toggleUpvote}
										data-tip={t('pageQuestion.upvoteTooltip')}
										data-for={`${question?.question_id}-upvote`}
									>
										<S.ConfettiTarget ref={confettiTargetRef} />
										<FontAwesomeIcon
											className={isUpvoteActive ? 'up active' : 'up'}
											icon={isUpvoteActive ? ['fas', 'up'] : ['far', 'up']}
										/>
										<Typography
											tag="span"
											className={isUpvoteActive && 'active'}
										>
											{formatNumber(upvoteCount) || '–'}
										</Typography>
									</ActionModule>

									{/* Action - Comment */}
									{question?.comments_allowed && (
										<LocaleLink to={`${location.pathname}#comments`} disableLocale>
											<ActionModule>
												<FontAwesomeIcon className="comment" icon={['far', 'comment']} />
												<Typography tag="span">{formatNumber(question?.comment_count) || '–'}</Typography>
											</ActionModule>
										</LocaleLink>
									)}

									{/* Action - Share */}
									<S.ShareActionContainer ref={containerRef}>

										{/* Share Button */}
										<ActionModule onClick={handleShare}>
											<FontAwesomeIcon className="share" icon={['far', 'share']} />
											<Typography tag="span">
												<Trans t={t} i18nKey="actions.share">
													Share
												</Trans>
											</Typography>
										</ActionModule>

										{/* Share Widget */}
										<S.ShareWidget
											className={showShareWidget ? 'animate show' : 'animate'}
											fromBottom={false}
											closeWidget={() => { setShowShareWidget(false); }}
											source={{
												type: SOURCES.QUESTION,
												id: question?.question_id
											}}
											content={{
												title: t('pageQuestion.shareTitle'),
												description: question?.question_locales[locale.localeBaseId],
												url: `https://community.ourwave.org${locale.localePath}/answer/${question?.slug}`
											}}
											items={[
												SHARE_OPTIONS.WHATSAPP,
												SHARE_OPTIONS.FACEBOOK,
												SHARE_OPTIONS.LINK,
												SHARE_OPTIONS.TWITTER,
												SHARE_OPTIONS.LINKEDIN,
												SHARE_OPTIONS.REDDIT,
												SHARE_OPTIONS.PINTEREST,
												SHARE_OPTIONS.EMAIL
											]}
										/>

									</S.ShareActionContainer>

									{/* Tooltip */}
									<ToolTip id={`${question?.question_id}-upvote`} weight="medium" />

								</S.ActionBar>

								{/* Comments Container */}
								<S.CommentsContainer>

									{/* Comment Signpost */}
									<S.CommentSignpost id="comments" />

									{/* Show comments if necessary */}
									{question?.comments_allowed ? (
										<>
											{/* Comment Creator */}
											<CommentCreator source={SOURCES.QUESTION} sourceId={question?.question_id} postCreateActions={(commentObj) => { setPendingComments([...pendingComments, commentObj]); }} />

											{/* Comment Feed */}
											<CommentFeed source={SOURCES.QUESTION} sourceId={question?.question_id} pendingComments={pendingComments} />
										</>
									) : (
										<S.CommentsDisabledLabel>
											<Typography tag="p" weight="medium" variation="2" center>
												<Trans t={t} i18nKey="pageQuestion.commentsDisabled">
													Replies are disabled for this question
												</Trans>
											</Typography>
										</S.CommentsDisabledLabel>
									)}
								</S.CommentsContainer>

							</S.QuestionCard>

							{/* Detail Page Panel */}
							<DetailPagePanel
								source={SOURCES.QUESTION}
								sourceId={question?.question_id}
								related={related}
								onRelatedClick={() => { moveNext(question); }}
								className="detailPanel"
							/>
						</>
					) : <ErrorComponent showMeta />}

					{/* Confetti */}
					{fireConfetti && (
						<ConfettiBurst
							fireAway={fireConfetti}
							targetRef={confettiTargetRef}
							force={0.1}
							duration={3000}
							particleCount={30}
						/>
					)}
				</S.Wrapper>
			</AppNavigation>
		</>
	);
};


/**
 * Configuration
 */

Question.propTypes = {
	meta: PropTypes.shape()
};
Question.defaultProps = {
	meta: {}
};


/**
 * Exports
 */

export default Question;
